import { Button, Card, CardBody, CardHeader, Row } from 'reactstrap';
import { useState } from 'react';
import AssignInspectorModal from './AssignInspectorModal';
import PropertyRejectionReasonModal from './PropertyRejectionReasonModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateStatusPropertyUnitById } from 'endpoints/builder/builderPropertyListingDetails';
import { toast } from 'react-toastify';

interface ActionProps {
    inspectionId: string;
    companyInspectionStatus?: string;
    isInspectionLoading?: boolean;
    setInspectionId: (id: string) => void;
    setPropertyUnitStatus?: (status: string) => void;
    propertyUnitId: string;
    setPropertyRejectionReason?: (reason: string) => void;
}

const Action = ({ inspectionId, companyInspectionStatus, isInspectionLoading, setInspectionId, setPropertyUnitStatus, propertyUnitId, setPropertyRejectionReason }: ActionProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);

    const queryClient = useQueryClient();

    const toggleModal = () => setModalOpen(!modalOpen);
    const toggleRejectModal = () => setRejectModalOpen(!rejectModalOpen);

    const updatePropertyUnitStatusMutation = useMutation({
        mutationFn: ({ propertyUnitId, data }: { propertyUnitId: string; data: { status: string; rejection_reason?: string } }) =>
            updateStatusPropertyUnitById(propertyUnitId, data),
        onSuccess: (data) => {
            if (data?.status_code === 200) {
                if (setPropertyUnitStatus) setPropertyUnitStatus(data?.data?.status);
                if (setPropertyRejectionReason) setPropertyRejectionReason(data?.data?.rejection_reason || '');
                if(data?.data?.status === 'rejected'){
                    // refetch the company inspection api
                    queryClient.invalidateQueries({ queryKey: ['propertyUnitInspectionDetails'] });
                };
                toast.success('Property unit status updated successfully');
                // Close modals on success
                setModalOpen(false);
                setRejectModalOpen(false);
            }
        },
        onError: (error) => {
            console.error('Error updating property unit status:', error);
            toast.error('Failed to update property unit status');
        }
    });
    // Calculate button label
    const getButtonLabel = () => {
        if (isInspectionLoading || updatePropertyUnitStatusMutation.isPending) return 'Loading...';
        if (inspectionId && companyInspectionStatus) return 'Make Live';
        return 'Approve & Assign Inspector';
    };

    // Handle button click based on current state
    const handleButtonClick = () => {
        const currentLabel = getButtonLabel();

        if (currentLabel === 'Approve & Assign Inspector') {
            // Open modal for assigning inspector
            toggleModal();
        } else if (currentLabel === 'Make Live') {
            // Handle make live action - update property unit status to public
            updatePropertyUnitStatusMutation.mutate({
                propertyUnitId,
                data: { status: 'public' }
            });
        }
    };

    // Handle property rejection
    const handleRejectProperty = (rejectionReason: string) => {
        updatePropertyUnitStatusMutation.mutate({
            propertyUnitId,
            data: {
                status: 'rejected',
                rejection_reason: rejectionReason
            }
        });
    };

    return (
        <Card>
            <CardHeader>
                <h6>Action</h6>
            </CardHeader>
            <CardBody>
                <div className="d-flex flex-column gap-3">
                    <Row className="px-3">
                        <Button
                            className='rounded-0'
                            onClick={handleButtonClick}
                            color='success'
                            disabled={isInspectionLoading || updatePropertyUnitStatusMutation.isPending || (!!companyInspectionStatus && companyInspectionStatus !== 'approved')}
                            style={{ 
                                cursor: (isInspectionLoading || updatePropertyUnitStatusMutation.isPending || (!!companyInspectionStatus && companyInspectionStatus !== 'approved')) 
                                    ? 'not-allowed' 
                                    : 'pointer' 
                            }}
                        >
                            {getButtonLabel()}
                        </Button>
                    </Row>
                    <Row className="px-3">
                        <Button
                            className='rounded-0'
                            onClick={toggleRejectModal}
                            color='danger'
                            disabled={isInspectionLoading || updatePropertyUnitStatusMutation.isPending}
                            style={{ 
                                cursor: (isInspectionLoading || updatePropertyUnitStatusMutation.isPending) 
                                    ? 'not-allowed' 
                                    : 'pointer' 
                            }}
                        >
                            {updatePropertyUnitStatusMutation.isPending ? 'Loading...' : 'Reject'}
                        </Button>
                    </Row>
                </div>
            </CardBody>
            <AssignInspectorModal isOpen={modalOpen} toggle={toggleModal} setInspectionId={setInspectionId} inspectionId={inspectionId} />
            <PropertyRejectionReasonModal
                isOpen={rejectModalOpen}
                toggle={toggleRejectModal}
                onConfirm={handleRejectProperty}
                isLoading={updatePropertyUnitStatusMutation.isPending}
            />
        </Card>
    );
};

export default Action;